import React, { useCallback, useEffect, useState } from "react";
import AddCategory from "../addCategory/AddCategory";
import {
  deleteCategoryById,
  findAllCategory,
} from "../../../../api/inventoryService";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryIcon from "@mui/icons-material/Category";
import SearchIcon from "@mui/icons-material/Search";
import { allowedReleaseKinds, releaseKindsSummary } from "../categoryReleaseTypes";

interface Category {
  _id: string;
  categoryName: string;
  releaseTypes?: string[];
  /** The clinic's built-in "Other": products land here when their category is deleted. */
  isDefault?: boolean;
}

/** Edit + delete for a normal row; the built-in Other row only says what it is. */
const RowActions: React.FC<{
  item: Category;
  onEdit: (id: string) => void;
  onDelete: (item: Category) => void;
}> = ({ item, onEdit, onDelete }) =>
  item.isDefault ? (
    <span
      className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-300"
      title="Products whose category is deleted move here"
    >
      Built-in
    </span>
  ) : (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => onEdit(item._id)}
        className="p-1.5 rounded-md bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
        title="Edit"
      >
        <EditIcon sx={{ fontSize: 16 }} />
      </button>
      <button
        onClick={() => onDelete(item)}
        className="p-1.5 rounded-md bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors"
        title="Delete"
      >
        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );

const AllCategory: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<Category[]>([]);
  const [id, setId] = useState<string>("");
  const [search, setSearch] = useState("");

  const getData = useCallback(() => {
    findAllCategory()
      .then((data) => setData(data as Category[]))
      .catch((error) => console.error(error?.response));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setId("");
    getData();
  }, [getData]);

  const openModal = useCallback(() => setOpen(true), []);

  const update = useCallback((id: string) => {
    setId(id);
    setOpen(true);
  }, []);

  const remove = useCallback(
    (item: Category) => {
      if (
        !window.confirm(
          `Delete "${item.categoryName}"?\n\nIts sub categories are removed and its products move to the "Other" category.`
        )
      )
        return;
      deleteCategoryById(item._id)
        .then(({ movedProducts, removedSubCategories }) => {
          const moved =
            movedProducts > 0
              ? ` ${movedProducts} product${movedProducts === 1 ? "" : "s"} moved to Other.`
              : "";
          const subs =
            removedSubCategories > 0
              ? ` ${removedSubCategories} sub categor${removedSubCategories === 1 ? "y" : "ies"} removed.`
              : "";
          toast.success(`"${item.categoryName}" deleted.${moved}${subs}`);
          getData();
        })
        .catch((error) => {
          console.error(error?.response);
          toast.error(error?.response?.data?.message || "Could not delete the category.");
        });
    },
    [getData]
  );

  const filteredData = data.filter((item) =>
    item.categoryName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Header Card */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 mb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
          <div className="relative flex-1 max-w-md">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              sx={{ fontSize: 16 }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search categories..."
              className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/40 rounded-lg">
              <CategoryIcon className="text-emerald-500 dark:text-emerald-300" sx={{ fontSize: 16 }} />
              <span className="font-bold text-xs text-emerald-600 dark:text-emerald-300">{data.length}</span>
              <span className="text-slate-500 dark:text-slate-400 text-[10px]">Categories</span>
            </div>
            <button
              onClick={openModal}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Add Category
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        {/* Mobile Cards */}
        <div className="md:hidden p-3 space-y-2">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <CategoryIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No categories found</p>
            </div>
          ) : (
            filteredData.map((item, i) => (
              <div key={item._id} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 border border-slate-100 dark:border-slate-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primaryLight rounded-full flex items-center justify-center text-white font-semibold text-xs">
                      {i + 1}
                    </div>
                    <div>
                      <span className="block font-semibold text-xs text-slate-800 dark:text-slate-100">{item.categoryName}</span>
                      <span className="block text-[10px] text-slate-500 dark:text-slate-400">
                        {releaseKindsSummary(allowedReleaseKinds(item))}
                      </span>
                    </div>
                  </div>
                  <RowActions item={item} onEdit={update} onDelete={remove} />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-600">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">No</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Category Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Released in</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-slate-400 dark:text-slate-500">
                    <CategoryIcon sx={{ fontSize: 36 }} className="mb-2" />
                    <p className="text-xs">No categories found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, i) => (
                  <tr key={item._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 bg-gradient-to-br from-primary to-primaryLight rounded-full flex items-center justify-center text-white text-[10px] font-semibold">
                          {item.categoryName.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-xs text-slate-800 dark:text-slate-100">{item.categoryName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                      {releaseKindsSummary(allowedReleaseKinds(item))}
                    </td>
                    <td className="px-3 py-2">
                      <RowActions item={item} onEdit={update} onDelete={remove} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && <AddCategory id={id} closeModal={closeModal} />}
    </>
  );
};

export default AllCategory;
